import { CalendarCheck, CalendarX2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AuthShell } from "@/components/auth/AuthShell";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageSpinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { readInvite, respondToInvite } from "@/lib/api/invites";
import { formatTimeRange } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import { useI18n } from "@/lib/i18n";

export function InvitePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState<string | null>(null);
  const { t, locale } = useI18n();

  const { data, loading, error } = useAsync(() => readInvite(token), [token]);

  const seo = (
    <Seo
      description="You have been invited to a BS Meet video meeting. Accept, decline, or join straight away. No account needed."
      noindex
      path={`/invite/${token}`}
      title={data === null ? "Invitation" : data.meeting.title}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <PageSpinner label={t("invite.opening")} />
      </>
    );
  }

  if (error !== null || data === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4">
        {seo}
        <EmptyState
          action={<Button onClick={() => void navigate("/")}>{t("notFound.home")}</Button>}
          description={t("invite.notFoundBody")}
          icon={CalendarX2}
          title={t("invite.notFound")}
        />
      </div>
    );
  }

  const { meeting, invitee } = data;
  const status = answered ?? invitee.status;

  const respond = async (next: "accepted" | "declined") => {
    setBusy(true);
    try {
      await respondToInvite(token, next);
      setAnswered(next);
      toast.show(
        next === "accepted" ? t("invite.seeYouThere") : t("invite.organizerTold"),
        "success",
      );
    } catch (error_) {
      toast.fail(error_);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      footer={
        <Link className="font-medium text-brand hover:underline" to={`/${meeting.roomSlug}`}>
          {t("invite.joinNow")}
        </Link>
      }
      subtitle={t("invite.invitedYou", { name: meeting.organizer.displayName })}
      title={meeting.title}
    >
      {seo}
      <div className="flex flex-col gap-4">
        <p className="flex items-center gap-2 text-sm text-ink">
          <CalendarCheck className="h-4 w-4 shrink-0 text-brand" />
          {formatTimeRange(meeting.startsAt, meeting.endsAt, locale)}
        </p>

        {meeting.description !== "" ? (
          <p className="text-sm whitespace-pre-wrap text-ink-muted">{meeting.description}</p>
        ) : null}

        {status === "pending" ? (
          <div className="flex gap-2">
            <Button
              className="flex-1"
              loading={busy}
              onClick={() => {
                void respond("accepted");
              }}
            >
              {t("invite.accept")}
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                void respond("declined");
              }}
              variant="secondary"
            >
              {t("invite.decline")}
            </Button>
          </div>
        ) : (
          <p className="rounded-xl bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
            {t("invite.answered", {
              status:
                status === "accepted" ? t("invite.status.accepted") : t("invite.status.declined"),
            })}
          </p>
        )}
      </div>
    </AuthShell>
  );
}
